import jsPDF from 'jspdf';

const fmtNum = (n) => Number(n || 0).toFixed(2);

// Cache logos per URL so we only fetch once per session
const logoCache = {};

async function loadLogo(url) {
  if (!url) return null;
  if (logoCache[url]) return logoCache[url];
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();

    // Read as data URL
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read logo'));
      reader.readAsDataURL(blob);
    });

    // Load into an Image to get natural dimensions
    const dimensions = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 1, height: 1 });
      img.src = dataUrl;
    });

    const result = { dataUrl, width: dimensions.width, height: dimensions.height };
    logoCache[url] = result;
    return result;
  } catch (e) {
    console.warn('Could not load logo:', e);
    return null;
  }
}

export async function generateInvoicePDF(invoice, bizInfo) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = 210;
  const margin = 18;
  const contentW = pageW - margin * 2;
  let y = 18;

  // ─── Load logo ───
  const logo = await loadLogo(bizInfo.logo_url || '');

  // ─── Colors ───
  const black = [0, 0, 0];
  const dark = [30, 30, 30];
  const mid = [80, 80, 80];
  const light = [130, 130, 130];
  const lineColor = [180, 180, 180];

  const drawLine = (yPos, color = lineColor, width = 0.3) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(width);
    doc.line(margin, yPos, pageW - margin, yPos);
  };

  const txt = (text, x, yPos, { size = 9, color = dark, style = 'normal', align = 'left', maxWidth = null } = {}) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const opts = { align };
    if (maxWidth) opts.maxWidth = maxWidth;
    doc.text(String(text ?? ''), x, yPos, opts);
  };

  // ═══════════════════════════════════════
  // LOGO (top-right, matching Xero placement)
  // ═══════════════════════════════════════
  if (logo) {
    try {
      // Constrain to max 15mm tall, max 40mm wide — maintain true aspect ratio
      const maxH = 15;
      const maxW = 40;
      const imgW = logo.width;
      const imgH = logo.height;

      // Calculate scale factors for each constraint, use the smaller one
      const scaleH = maxH / imgH;
      const scaleW = maxW / imgW;
      const scale = Math.min(scaleH, scaleW);

      const logoW = imgW * scale;
      const logoH = imgH * scale;

      // Position: right-aligned, top of page
      const logoX = pageW - margin - logoW;
      const logoY = y - 4;

      // Auto-detect image format from the data URL mime type
      let imgFormat = 'PNG';
      if (logo.dataUrl.includes('image/jpeg') || logo.dataUrl.includes('image/jpg')) {
        imgFormat = 'JPEG';
      } else if (logo.dataUrl.includes('image/webp')) {
        imgFormat = 'WEBP';
      }
      doc.addImage(logo.dataUrl, imgFormat, logoX, logoY, logoW, logoH);
    } catch (e) {
      console.warn('Could not embed logo in PDF:', e);
    }
  }

  // ═══════════════════════════════════════
  // HEADER: "INVOICE"
  // ═══════════════════════════════════════
  txt('INVOICE', margin, y, { size: 22, style: 'bold', color: black });

  y += 12;
  const headerTop = y;

  // ─── Left column: Bill To (client) ───
  const leftX = margin;
  let leftY = headerTop;

  txt(invoice.client_name || '', leftX, leftY, { size: 10, color: dark });
  leftY += 5;
  if (invoice.contact_name || invoice.client_name) {
    txt(`Attention: ${invoice.contact_name || invoice.client_name}`, leftX, leftY, { size: 9, color: mid });
    leftY += 4.5;
  }
  if (invoice.client_email) {
    doc.setTextColor(0, 0, 200);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(invoice.client_email, leftX, leftY);
    leftY += 4.5;
  }

  // ─── Centre column: Invoice meta ───
  const centreX = margin + contentW * 0.37;
  let centreY = headerTop;

  const metaFields = [
    ['Invoice Date', invoice.date || ''],
    ['Invoice Number', invoice.number || ''],
  ];
  if (invoice.notes) {
    metaFields.push(['Reference', invoice.notes]);
  }

  metaFields.forEach(([label, value]) => {
    txt(label, centreX, centreY, { size: 8, style: 'bold', color: dark });
    centreY += 4;
    txt(value, centreX, centreY, { size: 9, color: mid });
    centreY += 6;
  });

  // ─── Right column: Your business details (below logo) ───
  const rightX = pageW - margin;
  // Start below the logo if present, otherwise align with other columns
  let rightY = logo ? headerTop + 18 : headerTop;

  if (bizInfo.name) {
    txt(bizInfo.name, rightX, rightY, { size: 10, style: 'bold', color: dark, align: 'right' });
    rightY += 5;
  }
  if (bizInfo.address) {
    const addressLines = bizInfo.address.split(',').map(s => s.trim());
    addressLines.forEach(line => {
      txt(line, rightX, rightY, { size: 9, color: mid, align: 'right' });
      rightY += 4.5;
    });
  }
  if (bizInfo.email) {
    doc.setTextColor(0, 0, 200);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(bizInfo.email, rightX, rightY, { align: 'right' });
    rightY += 5;
  }
  if (bizInfo.abn) {
    txt(`ABN ${bizInfo.abn}`, rightX, rightY, { size: 9, color: mid, align: 'right' });
    rightY += 4.5;
  }

  y = Math.max(leftY, centreY, rightY) + 10;

  // ═══════════════════════════════════════
  // LINE ITEMS TABLE
  // ═══════════════════════════════════════
  const colDesc = margin;
  const colQty = margin + contentW * 0.42;
  const colPrice = margin + contentW * 0.56;
  const colDiscount = margin + contentW * 0.70;
  const colGST = margin + contentW * 0.82;
  const colAmount = pageW - margin;

  // Column headers
  txt('Description', colDesc, y, { size: 8, style: 'bold', color: dark });
  txt('Quantity', colQty, y, { size: 8, style: 'bold', color: dark, align: 'right' });
  txt('Unit Price', colPrice, y, { size: 8, style: 'bold', color: dark, align: 'right' });
  txt('Discount', colDiscount, y, { size: 8, style: 'bold', color: dark, align: 'right' });
  txt('GST', colGST, y, { size: 8, style: 'bold', color: dark, align: 'right' });
  txt('Amount AUD', colAmount, y, { size: 8, style: 'bold', color: dark, align: 'right' });

  y += 2;
  drawLine(y, lineColor, 0.4);
  y += 6;

  // Table rows
  const items = invoice.items || [];
  items.forEach((item) => {
    if (y > 250) { doc.addPage(); y = 20; }

    const lineTotal = (item.qty || 0) * (item.rate || 0);

    txt(item.desc || '', colDesc, y, { size: 9, color: dark, maxWidth: contentW * 0.38 });
    txt(fmtNum(item.qty), colQty, y, { size: 9, color: dark, align: 'right' });
    txt(fmtNum(item.rate), colPrice, y, { size: 9, color: dark, align: 'right' });
    txt('0.00%', colDiscount, y, { size: 9, color: dark, align: 'right' });
    txt('0', colGST, y, { size: 9, color: dark, align: 'right' });
    txt(fmtNum(lineTotal), colAmount, y, { size: 9, color: dark, align: 'right' });

    y += 7;
  });

  // Colored divider lines (matching Xero's style)
  y += 2;
  doc.setDrawColor(80, 80, 200);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  doc.setDrawColor(200, 100, 80);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 0.5, pageW - margin, y + 0.5);

  y += 8;

  // ═══════════════════════════════════════
  // TOTALS (right-aligned)
  // ═══════════════════════════════════════
  const totalsLabelX = margin + contentW * 0.65;
  const totalsValX = pageW - margin;
  const subtotal = items.reduce((s, it) => s + (it.qty || 0) * (it.rate || 0), 0);
  const paidAmount = invoice.status === 'paid' ? subtotal : 0;
  const balance = subtotal - paidAmount;

  const totalsRows = [
    ['Subtotal', fmtNum(subtotal), false],
    ['TOTAL GST', '0.00', false],
    ['TOTAL AUD', fmtNum(subtotal), true],
    ['Paid', fmtNum(paidAmount), false],
    ['Balance', fmtNum(balance), true],
  ];

  totalsRows.forEach(([label, value, bold]) => {
    txt(label, totalsLabelX, y, { size: 9, style: bold ? 'bold' : 'normal', color: dark, align: 'right' });
    txt(value, totalsValX, y, { size: 9, style: bold ? 'bold' : 'normal', color: dark, align: 'right' });
    y += 5.5;
  });

  y += 6;

  // ═══════════════════════════════════════
  // DUE DATE + PAYMENT DETAILS
  // ═══════════════════════════════════════
  txt('Due Date', margin, y, { size: 10, style: 'bold', color: dark });
  txt(invoice.due_date || '', margin + 35, y, { size: 10, color: dark });
  y += 8;

  if (bizInfo.bsb || bizInfo.account_number) {
    txt('Electronic Funds Transfer', margin, y, { size: 9, color: mid });
    y += 5;
    if (bizInfo.account_name || bizInfo.name) {
      txt(bizInfo.account_name || bizInfo.name, margin, y, { size: 9, color: dark });
      y += 4.5;
    }
    if (bizInfo.bsb) {
      txt(`BSB: ${bizInfo.bsb}`, margin, y, { size: 9, color: dark });
      y += 4.5;
    }
    if (bizInfo.account_number) {
      txt(`ACC: ${bizInfo.account_number}`, margin, y, { size: 9, color: dark });
      y += 4.5;
    }
  }

  y += 10;

  // ═══════════════════════════════════════
  // PAYMENT ADVICE
  // ═══════════════════════════════════════
  if (y > 220) { doc.addPage(); y = 20; }

  txt('PAYMENT ADVICE', margin, y, { size: 18, style: 'bold', color: black });
  y += 10;

  txt('To:', margin, y, { size: 8, color: light });
  const paLeftX = margin + 12;

  if (bizInfo.name) {
    txt(bizInfo.name, paLeftX, y, { size: 9, color: dark });
    y += 4.5;
  }
  if (bizInfo.name) {
    txt(`Attention: ${bizInfo.name}`, paLeftX, y, { size: 9, color: mid });
    y += 4.5;
  }
  if (bizInfo.address) {
    const addressLines = bizInfo.address.split(',').map(s => s.trim());
    addressLines.forEach(line => {
      txt(line, paLeftX, y, { size: 9, color: mid });
      y += 4.5;
    });
  }
  if (bizInfo.email) {
    doc.setTextColor(0, 0, 200);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(bizInfo.email, paLeftX, y);
    y += 8;
  }

  // Remittance table
  drawLine(y, lineColor, 0.3);
  y += 5;

  const paCol1 = margin;
  const paCol2 = margin + contentW * 0.28;
  const paCol3 = margin + contentW * 0.52;
  const paCol4 = pageW - margin;

  txt('Invoice Number', paCol1, y, { size: 8, style: 'bold', color: dark });
  txt('Invoice Date', paCol2, y, { size: 8, style: 'bold', color: dark });
  txt('Invoice Total', paCol3, y, { size: 8, style: 'bold', color: dark, align: 'right' });
  txt('Amount Due', paCol4, y, { size: 8, style: 'bold', color: dark, align: 'right' });

  y += 2;
  drawLine(y, lineColor, 0.3);
  y += 5;

  txt(invoice.number || '', paCol1, y, { size: 9, color: dark });
  txt(invoice.date || '', paCol2, y, { size: 9, color: dark });
  txt(fmtNum(subtotal), paCol3, y, { size: 9, color: dark, align: 'right' });
  txt(fmtNum(balance), paCol4, y, { size: 9, style: 'bold', color: dark, align: 'right' });

  y += 2;
  drawLine(y, lineColor, 0.3);

  return doc;
}

export async function downloadInvoicePDF(invoice, bizInfo) {
  const doc = await generateInvoicePDF(invoice, bizInfo);
  doc.save(`${invoice.number || 'invoice'}.pdf`);
}

export async function emailInvoicePDF(invoice, bizInfo) {
  const doc = await generateInvoicePDF(invoice, bizInfo);
  const pdfBlob = doc.output('blob');

  // Download the PDF first
  const url = URL.createObjectURL(pdfBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${invoice.number || 'invoice'}.pdf`;
  a.click();
  URL.revokeObjectURL(url);

  // Build email
  const total = (invoice.items || []).reduce((s, it) => s + (it.qty || 0) * (it.rate || 0), 0);
  const subject = encodeURIComponent(`Invoice ${invoice.number} from ${bizInfo.name || ''}`);
  const body = encodeURIComponent(
    `Hi ${invoice.client_name},\n\n` +
    `Please find attached invoice ${invoice.number} for $${fmtNum(total)}.\n\n` +
    (invoice.due_date ? `Payment is due by ${invoice.due_date}.\n\n` : '') +
    (bizInfo.bsb && bizInfo.account_number
      ? `Payment details:\n` +
        (bizInfo.account_name || bizInfo.name ? `Name: ${bizInfo.account_name || bizInfo.name}\n` : '') +
        `BSB: ${bizInfo.bsb}\n` +
        `ACC: ${bizInfo.account_number}\n\n`
      : '') +
    `Thank you for your business.\n\n` +
    `Kind regards,\n${bizInfo.name || ''}`
  );

  const mailto = `mailto:${invoice.client_email || ''}?subject=${subject}&body=${body}`;

  setTimeout(() => {
    window.location.href = mailto;
  }, 500);
}
